/**
 * CoachingDateTimeSelector
 * Right panel of the coaching booking modal: calendar + time slots + message + book button
 */

import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib"
import { useCoachingAvailability } from "../hooks/useCoachingBooking"
import type { CoachingAvailabilitySlot } from "../services/coaching.service"
import { formatTime, toDateString } from "../utils/format"

interface CoachingDateTimeSelectorProps {
  coachingSessionId: string
  expertName: string | null
  onBook: (availabilityId: string, message: string) => void
  isBooking: boolean
  onRequestTime: () => void
  heading?: string
  actionLabel?: string
  actioningLabel?: string
  isReschedule?: boolean
}

function formatSelectedDate(date: Date, slot: CoachingAvailabilitySlot | undefined): string {
  if (!slot) return ""
  const dayName = date.toLocaleDateString("en-US", { weekday: "short" })
  const month = date.toLocaleDateString("en-US", { month: "short" })
  const day = date.getDate()
  const year = date.getFullYear()
  return `${dayName}, ${month} ${day}, ${year} at ${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`
}

// TimeSlotGrid component inline for coaching
interface TimeSlotGridProps {
  slots: CoachingAvailabilitySlot[]
  selectedSlotId: string | null
  onSelectSlot: (slotId: string) => void
}

function TimeSlotGrid({ slots, selectedSlotId, onSelectSlot }: TimeSlotGridProps) {
  const uniqueTimeSlots = useMemo(() => {
    if (slots.length === 0) return []

    const timeMap = new Map<string, { timeLabel: string; slotIds: string[]; anyBooked: boolean }>()

    for (const slot of slots) {
      const timeKey = `${slot.startTime}-${slot.endTime}`
      const timeLabel = `${formatTime(slot.startTime)}-${formatTime(slot.endTime)}`

      if (!timeMap.has(timeKey)) {
        timeMap.set(timeKey, { timeLabel, slotIds: [], anyBooked: false })
      }

      const entry = timeMap.get(timeKey)
      if (entry) {
        entry.slotIds.push(slot.id)
        if (slot.isBooked) {
          entry.anyBooked = true
        }
      }
    }

    return Array.from(timeMap.values())
  }, [slots])

  if (uniqueTimeSlots.length === 0) {
    return null
  }

  // Find if any slot in the group is selected
  const getIsSelected = (slotIds: string[]) => slotIds.includes(selectedSlotId || "")

  return (
    <div className="flex flex-wrap gap-2 mt-5 max-h-[200px] overflow-y-auto pr-1">
      {uniqueTimeSlots.map(({ timeLabel, slotIds, anyBooked }, index) => {
        const isSelected = getIsSelected(slotIds)
        // Use the first available slot ID for selection, or first slot ID if all are booked
        const slotIdForAction =
          slotIds.find(id => {
            const slot = slots.find(s => s.id === id)
            return slot && !slot.isBooked
          }) || slotIds[0]

        return (
          <button
            key={`${timeLabel}-${index}`}
            type="button"
            disabled={anyBooked}
            onClick={() => !anyBooked && onSelectSlot(slotIdForAction)}
            className={cn(
              "flex h-[35px] px-[11px] py-[6px] rounded-[10px] items-center justify-center text-[13px] font-semibold leading-5.5 transition-colors",
              isSelected && "bg-brand-terracotta text-my-rituality-bg",
              !isSelected &&
                !anyBooked &&
                "border border-connect-cream text-connect-cream hover:bg-connect-cream/10",
              anyBooked &&
                "cursor-not-allowed bg-connect-cream/5 text-connect-cream/30 line-through",
            )}
          >
            {timeLabel}
          </button>
        )
      })}
    </div>
  )
}

export function CoachingDateTimeSelector({
  coachingSessionId,
  expertName,
  onBook,
  isBooking,
  onRequestTime,
  heading,
  actionLabel,
  actioningLabel,
  isReschedule,
}: CoachingDateTimeSelectorProps) {
  const now = new Date()
  const [currentMonth, setCurrentMonth] = useState(now)
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [message, setMessage] = useState("")

  const month = currentMonth.getMonth() + 1
  const year = currentMonth.getFullYear()

  const { data: slots = [], isLoading } = useCoachingAvailability(coachingSessionId, month, year)

  // All dates that have any slots (even fully booked) -- so user can select
  // and see "no available times" rather than having the date disabled
  const datesWithSlots = useMemo(() => new Set(slots.map(s => s.date)), [slots])

  const selectedDateStr = selectedDate ? toDateString(selectedDate) : null
  const slotsForDate = selectedDateStr ? slots.filter(s => s.date === selectedDateStr) : []
  const selectedSlot = slots.find(s => s.id === selectedSlotId)
  const hasAvailability = slots.some(s => !s.isBooked)

  function handleDateSelect(date: Date | undefined) {
    setSelectedDate(date)
    setSelectedSlotId(null)
  }

  function handleMonthChange(date: Date) {
    setCurrentMonth(date)
    setSelectedDate(undefined)
    setSelectedSlotId(null)
  }

  function handleBook() {
    if (!selectedSlotId) return
    onBook(selectedSlotId, message)
  }

  return (
    <div className="flex min-w-0 flex-col p-5 pr-5 lg:w-[545px]">
      <h3 className="mb-5 lg:mt-3.75 text-[18px] font-semibold text-connect-cream">
        {heading ?? "Select Date & Time"}
      </h3>

      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        month={currentMonth}
        onMonthChange={handleMonthChange}
        disabled={date => !datesWithSlots.has(toDateString(date))}
        className="w-full rounded-[15px] p-5"
        classNames={{
          month_caption: "flex items-center h-[24px]",
          caption_label: "text-[16px] font-semibold leading-normal text-connect-cream",
          nav: "absolute right-0 top-0 flex items-center gap-[5px]",
          button_previous: "size-[24px] p-0",
          button_next: "size-[24px] p-0",
          weekdays: "flex w-full text-xs justify-between",
          weekday:
            "size-[44px] h-[18px] flex items-center justify-center text-[12px] font-semibold uppercase text-connect-cream/70",
          week: "mt-[5px] flex w-full justify-between",
          day: "size-[44px] text-[20px] leading-6.25 tracking-[-0.45px] p-0 font-normal aspect-square",
        }}
      />

      {isLoading && (
        <p className="text-center text-[13px] text-connect-cream/50">Loading availability...</p>
      )}

      <div className="flex flex-col mt-7.5">
        <div className="text-sm font-semibold text-[#F2F0EBE6] flex justify-between items-center">
          <span className="text-[18px] leading-normal">Available Times</span>
          {selectedSlot && selectedDate && (
            <span className="font-semibold">{formatSelectedDate(selectedDate, selectedSlot)}</span>
          )}
        </div>

        {!selectedDate && hasAvailability && !isLoading && (
          <p className="text-sm leading-6.25 mt-2.5 text-[#F2F0EB]">
            Please select a date above to see available times.
          </p>
        )}

        {!hasAvailability && !isLoading && (
          <div className="text-sm mt-2.5 leading-6.25 text-[#F2F0EB] flex flex-col">
            <p>No sessions are available for the selected month.</p>

            <span>
              Select another month or{" "}
              <button type="button" onClick={onRequestTime} className="font-semibold underline">
                Request a Time
              </button>
              .
            </span>
          </div>
        )}

        {selectedDate && slotsForDate.length > 0 && (
          <TimeSlotGrid
            slots={slotsForDate}
            selectedSlotId={selectedSlotId}
            onSelectSlot={setSelectedSlotId}
          />
        )}

        {selectedDate && slotsForDate.length === 0 && hasAvailability && !isLoading && (
          <p className="text-[13px] text-connect-cream/50">
            No time slots available for this date.
          </p>
        )}
      </div>

      {selectedSlot && selectedDate && (
        <div className="mt-7.5 flex flex-col gap-7.5">
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={`Message to ${expertName || "Coach"}`}
            maxLength={2000}
            rows={3}
            className="w-full resize-none rounded-[10px] border border-connect-cream/20 bg-[#1B1B1B] px-4 py-3 text-[13px] text-connect-cream placeholder:text-connect-cream/30 focus:border-connect-cream/40 focus:outline-none"
          />

          <div className="flex items-center justify-center">
            <Button
              variant="default"
              type="button"
              onClick={handleBook}
              disabled={isBooking}
              className={cn(
                "h-[50px] w-full py-[15px] rounded-full text-sm font-semibold uppercase transition-colors",
                "bg-connect-cream text-my-rituality-bg",
                "hover:bg-connect-cream-hover",
                "disabled:opacity-50",
              )}
            >
              {isBooking ? (actioningLabel ?? "Booking...") : (actionLabel ?? "Book Session")}
            </Button>
          </div>
        </div>
      )}

      {selectedDate && selectedSlot && !isReschedule && (
        <div className="mt-auto pt-[15px]">
          <p className="text-center text-[13px] text-connect-cream/50">
            Need a Different Time?{" "}
            <button
              type="button"
              onClick={onRequestTime}
              className="font-semibold text-connect-cream underline"
            >
              Request a Time
            </button>
          </p>
        </div>
      )}
    </div>
  )
}
